import { onCleanup, mergeProps } from 'solid-js';
import { provide } from 'component-register';
import {
  customShadowlessElement,
  correctElementType,
  type CorrectComponentType,
} from '@brytdesigns/web-component-core/utils';

import { TooltipContext, useTooltip, type ContextProps } from './tooltip-context';
import './tooltip.css';

const FOCUSABLE_SELECTOR =
  'button, a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const HOVER_OPEN_DELAY_MS = 150;
const TOUCH_SUPPRESS_MS = 500;

const TooltipContextElement: CorrectComponentType<ContextProps> = (props, { element }) => {
  provide(TooltipContext, mergeProps(props, { element }), element);
};

type EmptyProps = Record<string, never>;

const TooltipTriggerElement: CorrectComponentType<EmptyProps> = (_props, { element }) => {
  const [, actions] = useTooltip(element);
  const focusable = element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  if (!focusable) {
    console.warn('tooltip-trigger: no focusable descendant found; tooltip will not be accessible');
    return;
  }
  actions.registerTrigger(element, focusable);

  let lastTouchAt = 0;
  const recentlyTouched = () => Date.now() - lastTouchAt < TOUCH_SUPPRESS_MS;

  const onPointerDown = (event: PointerEvent) => {
    if (event.pointerType !== 'touch') return;
    lastTouchAt = Date.now();
    actions.toggle();
  };
  const onMouseEnter = () => {
    if (recentlyTouched()) return;
    actions.show({ delay: HOVER_OPEN_DELAY_MS });
  };
  const onMouseLeave = () => {
    if (recentlyTouched()) return;
    actions.hide();
  };
  const onFocusIn = () => {
    if (recentlyTouched()) return;
    actions.show();
  };
  const onFocusOut = () => {
    if (recentlyTouched()) return;
    actions.hide();
  };

  element.addEventListener('pointerdown', onPointerDown);
  element.addEventListener('mouseenter', onMouseEnter);
  element.addEventListener('mouseleave', onMouseLeave);
  element.addEventListener('focusin', onFocusIn);
  element.addEventListener('focusout', onFocusOut);

  onCleanup(() => {
    element.removeEventListener('pointerdown', onPointerDown);
    element.removeEventListener('mouseenter', onMouseEnter);
    element.removeEventListener('mouseleave', onMouseLeave);
    element.removeEventListener('focusin', onFocusIn);
    element.removeEventListener('focusout', onFocusOut);
  });
};

const TooltipContentElement: CorrectComponentType<EmptyProps> = (_props, { element }) => {
  const [, actions] = useTooltip(element);
  actions.registerContent(element);
};

const defaultContextProps: ContextProps = { placement: 'top', offset: 6, open: false };

customShadowlessElement('tooltip-context', defaultContextProps, correctElementType(TooltipContextElement));
customShadowlessElement('tooltip-trigger', {}, correctElementType(TooltipTriggerElement));
customShadowlessElement('tooltip-content', {}, correctElementType(TooltipContentElement));
